import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeftRight,
  Receipt,
  Wallet,
  Building2,
  ShieldCheck,
  Settings,
  LogOut,
  Menu,
  X,
  Landmark,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

interface MainLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  name: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
}

const navItems: NavItem[] = [
  { name: "Buy & Sell", path: "/", icon: ArrowLeftRight },
  { name: "Transactions", path: "/transactions", icon: Receipt },
  { name: "Wallets", path: "/wallets", icon: Wallet },
  { name: "Bank Accounts", path: "/bank-accounts", icon: Building2 },
  { name: "Static Addresses", path: "/static-addresses", icon: Landmark },
  { name: "KYC", path: "/kyc", icon: ShieldCheck },
  { name: "Profile", path: "/profile", icon: Settings },
];

export function MainLayout({ children }: MainLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
      navigate("/login");
    } catch (error) {
      navigate("/login");
    } finally {
      setIsLoggingOut(false);
    }
  };

  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-[var(--bg-secondary)] border-b border-[var(--border-primary)] z-40 backdrop-blur-md">
        <div className="flex items-center justify-between p-[var(--space-l)]">
          <Link to="/" className="flex items-center gap-[var(--space-s)]">
            <img
              src="/assets/pouchLogo.png"
              alt="PouchPay Logo"
              className="w-8 h-8 object-contain"
            />
            <h1 className="text-[var(--font-size-h3)] font-[var(--font-weight-bold)] text-[var(--fg-1)] font-[var(--font-display)]">
              PouchPay
            </h1>
          </Link>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-[var(--space-s)] rounded-[var(--radius-m)] hover:bg-[var(--bg-elevated)] text-[var(--fg-2)] transition-colors duration-[var(--dur)]"
            aria-label="Toggle menu"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Backdrop for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-40 md:hidden backdrop-blur-sm transition-opacity duration-[var(--dur)]"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex pt-16 md:pt-0">
        {/* Sidebar */}
        <aside
          className={`fixed md:static inset-y-0 left-0 z-50 w-[var(--sidebar-width)] bg-[var(--bg-secondary)] border-r border-[var(--border-primary)] transform transition-transform duration-[var(--dur-slow)] flex flex-col ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
          }`}
          style={{ width: "var(--sidebar-width)" }}
        >
          {/* Logo/Brand - Desktop only */}
          <div className="hidden md:flex items-center p-[var(--space-xl)] border-b border-[var(--border-primary)]">
            <Link to="/" className="flex items-center gap-[var(--space-m)]">
              <img
                src="/assets/pouchLogo.png"
                alt="PouchPay Logo"
                className="w-10 h-10 object-contain"
              />
              <h1 className="text-[var(--font-size-h3)] font-[var(--font-weight-bold)] text-[var(--fg-1)] font-[var(--font-display)]">
                PouchPay
              </h1>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-[var(--space-l)] space-y-[var(--space-xs)] overflow-y-auto">
            {navItems.map((item) => {
              const active = isActive(item.path);
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-[var(--space-m)] px-[var(--space-m)] py-[var(--space-m)]
                    rounded-[var(--radius-m)] transition-all duration-[var(--dur)] font-[var(--font-weight-medium)]
                    ${
                      active
                        ? "bg-[var(--accent-cyan)] text-[var(--primary-ink)] shadow-[var(--glow-cyan)]"
                        : "text-[var(--fg-2)] hover:bg-[var(--bg-elevated)] hover:text-[var(--fg-1)]"
                    }
                  `}
                >
                  <Icon
                    className={`w-5 h-5 ${active ? "opacity-100" : "opacity-70"}`}
                  />
                  <span className="text-[var(--font-size-body)]">
                    {item.name}
                  </span>
                </Link>
              );
            })}
          </nav>

          {/* User Menu / Logout */}
          <div className="p-[var(--space-l)] border-t border-[var(--border-primary)]">
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className={`
                flex items-center gap-[var(--space-m)] w-full px-[var(--space-m)] py-[var(--space-m)]
                rounded-[var(--radius-m)] transition-all duration-[var(--dur)] font-[var(--font-weight-medium)]
                text-[var(--font-size-body)]
                ${
                  isLoggingOut
                    ? "bg-[var(--bg-elevated)] text-[var(--fg-4)] cursor-not-allowed opacity-50"
                    : "text-[var(--fg-2)] hover:bg-[var(--accent-danger)] hover:text-white hover:shadow-[0_0_20px_-8px_var(--accent-danger)]"
                }
              `}
            >
              <LogOut className="w-5 h-5" />
              <span>{isLoggingOut ? "Logging out..." : "Logout"}</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          <div className="container mx-auto px-[var(--space-l)] pr-0! py-[var(--space-xl)] md:p-[var(--space-2xl)] max-w-7xl">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
